import { ShopBookClient, ProxyClient } from '@delphi-direct/proxy-api-client'
import type { QueriesPaymentPrice } from '@delphi-direct/proxy-api-client/dist/ModelAPIProxy'
import { PROXY_BASE_URL, PROXY_DOMAIN_KEY, PROXY_OVERRIDE_DOMAIN } from '@/config'
import type { BookingObject } from './createBooking/types'
import type { RequestPaymentPrices } from './price-booking/price-booking'
import { maxTime, minTime } from './config-date'

const shopAndBookClient = new ShopBookClient(
  PROXY_BASE_URL,
  PROXY_DOMAIN_KEY,
  PROXY_OVERRIDE_DOMAIN,
)

const proxyClient = new ProxyClient(PROXY_BASE_URL, PROXY_DOMAIN_KEY, PROXY_OVERRIDE_DOMAIN)

export const getLocationsAvailability = ({
  locations,
  date,
  attendees,
  languageCode = '',
}: {
  locations: string[]
  date: Date
  attendees: number
  languageCode?: string
}) => {
  const apiLocationArray = locations.map((loc) => ({ id: loc }))
  return shopAndBookClient.getLocations({
    startDate: date,
    endDate: date,
    numberOfAttendees: attendees,
    locations: apiLocationArray,
    languageCode: languageCode,
  })
}

export const getVenuesAvailability = ({
  location,
  date,
  attendees,
  bestFit = false,
  languageCode = '',
}: {
  location: string
  date: Date
  attendees: number
  bestFit?: boolean
  languageCode?: string
}) => {
  return shopAndBookClient
    .getFunctionRooms({
      startDate: date,
      endDate: date,
      numberOfAttendees: attendees,
      bestFit: bestFit,
      locations: [
        {
          id: location,
        },
      ],
      languageCode: languageCode,
    })
    .then((result) => {
      return (
        result.locations[0]?.packages.map((packageItem) => {
          const meetingRooms = new Map<
            string,
            {
              functionRoomSetups: string[]
              minRentalPerPerson: number
              taxes: string
              searchTimeSlot: {
                startTime: string
                endTime: string
              }[]
              eventsIds: {
                id: string
                timeSlot: {
                  startTime: string
                  endTime: string
                }[]
                eventClassificationId: string
              }[]
            }
          >()
          const { functionRoomSetups, ...otherPackageData } = packageItem

          functionRoomSetups.forEach((roomSetup) => {
            roomSetup.functionRooms.forEach((functionRoom) => {
              functionRoom.timeSlots.forEach((timeSlot) => {
                timeSlot.days.forEach((day) => {
                  // once calculated, the price of a meeting room in a package
                  // is always the same, so no need to re-do the sum
                  if (meetingRooms.has(day.id)) {
                    // Check if this roomSetup.name is not already define
                    if (
                      meetingRooms.get(day.id)?.functionRoomSetups.indexOf(roomSetup.name) === -1
                    ) {
                      meetingRooms.get(day.id)?.functionRoomSetups.push(roomSetup.name)
                    }
                    // Collect all the timeSlot
                    day.events.forEach((event) => {
                      meetingRooms.get(day.id)?.eventsIds.forEach((meetingEvent, index) => {
                        if (
                          meetingEvent.id === event.id &&
                          meetingEvent.eventClassificationId === event.eventClassificationId
                        ) {
                          // Avoid doublon between startTim and endTime
                          if (
                            meetingRooms
                              .get(day.id)
                              ?.eventsIds.at(index)
                              ?.timeSlot.filter(
                                (el) =>
                                  el.startTime === event.startTime && el.endTime === event.endTime,
                              ).length === 0
                          ) {
                            meetingRooms.get(day.id)?.eventsIds.at(index)?.timeSlot.push({
                              startTime: event.startTime,
                              endTime: event.endTime,
                            })
                          }
                        }
                      })
                    })
                    return
                  }

                  const sumRental = day.events.reduce((prevPrice, eventItem) => {
                    if (!eventItem.rental) {
                      return prevPrice
                    }

                    let rental = eventItem.rental

                    if (eventItem.rentalCalculation === 'PerEvent') {
                      rental = rental / attendees
                    }
                    // TODO calculate "PerHour"

                    return prevPrice + rental
                  }, 0)
                  meetingRooms.set(day.id, {
                    functionRoomSetups: [roomSetup.name],
                    minRentalPerPerson: sumRental,
                    taxes: day.events[0]?.isInclusive ? 'inclusive' : 'exclusive',
                    // Will filled laster (just below)
                    searchTimeSlot: [],
                    eventsIds: day.events.map((event) => ({
                      id: event.id,
                      timeSlot: [
                        {
                          startTime: event.startTime,
                          endTime: event.endTime,
                        },
                      ],
                      eventClassificationId: event.eventClassificationId,
                    })),
                  })
                })
              })
            })
          })
          // Filled slotTime label for the searchBar
          meetingRooms.forEach((_, key) => {
            // Define default index Zero
            let currentIndex: number = 0
            do {
              // Get current index
              currentIndex = meetingRooms.get(key)?.searchTimeSlot.length ?? 0
              let minStart = ''
              let maxEnd = ''
              // Retrieve the min and max for the current index
              meetingRooms.get(key)?.eventsIds.forEach((eventId) => {
                if (eventId.timeSlot.at(currentIndex)) {
                  minStart = minTime(eventId.timeSlot.at(currentIndex)?.startTime ?? '', minStart)
                  maxEnd = maxTime(eventId.timeSlot.at(currentIndex)?.endTime ?? '', maxEnd)
                }
              })
              // If any min and max has been found, push (it's will produise a new loop occuration)
              if (minStart !== '' && maxEnd !== '') {
                meetingRooms.get(key)?.searchTimeSlot.push({ startTime: minStart, endTime: maxEnd })
              }
            } while (currentIndex !== (meetingRooms.get(key)?.searchTimeSlot.length ?? 0))
          })

          const cheapestRentalPerPerson = [...meetingRooms.values()].reduce(
            (min, current) => (current.minRentalPerPerson < min ? current.minRentalPerPerson : min),
            Infinity,
          )

          return {
            ...otherPackageData,
            priceMinPerPerson: (otherPackageData.eventPriceTotal ?? 0) + cheapestRentalPerPerson,
            meetingRooms,
          }
        }) || []
      )
    })
}

export const getPackageDetails = (
  packageId: string,
  attendees: number,
  locationID: string,
  languageCode?: string,
) => {
  return shopAndBookClient.getPackageDetails({
    id: packageId,
    numberOfAttendees: attendees,
    location: {
      id: locationID,
    },
    languageCode: languageCode,
  })
}

export const getCartPrices = (
  locationId: string,
  bookingObject: RequestPaymentPrices,
  queries?: QueriesPaymentPrice,
) => {
  // TODO update sdk, because sdk type requires to receive startTime endTime for events => Antoine: No, this is fake news
  return proxyClient.getPaymentPricesDirectBook(locationId, bookingObject, queries)
}

export const getPaymentUrl = (locationId: string, bookingObject: BookingObject) => {
  console.debug('Booking object sent to API', JSON.stringify(bookingObject))
  return proxyClient.getPaymentLinkDirectBook(locationId, bookingObject)
}

export const getPaymentStatus = (
  folderId: string,
  paymentId: string,
  xObjectID?: string,
  xObjectType?: string,
) => {
  return proxyClient.getPaymentStatusDirectBook(folderId, paymentId, xObjectID, xObjectType)
}

export type ResponseLocations = Awaited<ReturnType<typeof shopAndBookClient.getLocations>>
